import { useState, useCallback } from 'react';
import { useCoreStore } from '../stores/coreStore';
import { detectStopCommand } from '../utils/ai';
import { INFO_GATHERING } from '../constants';

/**
 * Custom hook for info gathering workflow
 * @param {Object} api - useAnthropicAPI hook instance
 * @returns {Object} Info gathering functions and state
 */
export const useInfoGathering = (api) => {
  const setNodes = useCoreStore((s) => s.setNodes);
  const setMessages = useCoreStore((s) => s.setMessages);

  const [infoGatheringNode, setInfoGatheringNode] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [collectedInfo, setCollectedInfo] = useState([]);

  /**
   * Start info gathering for a node
   */
  const startInfoGathering = useCallback(async (node) => {
    setInfoGatheringNode(node);
    setCollectedInfo([]);

    setMessages(prev => [...prev, {
      role: 'assistant',
      content: '🔍 Fammi capire meglio il contesto...',
      isInfoGathering: true
    }]);

    try {
      const question = await api.generateNextQuestion(node, []);
      if (question) {
        setCurrentQuestion(question);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `💭 ${question}`,
          isInfoGathering: true
        }]);
      }
    } catch (error) {
      console.error('Errore generazione domanda:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Non riesco a generare la domanda. Riprova.'
      }]);
      setInfoGatheringNode(null);
    }
  }, [api, setMessages]);

  /**
   * Process user answer to current question
   */
  const answerQuestion = useCallback(async (answer) => {
    const updatedInfo = [...collectedInfo, {
      question: currentQuestion,
      answer: answer.trim()
    }];
    setCollectedInfo(updatedInfo);

    if (updatedInfo.length >= INFO_GATHERING.MAX_QUESTIONS) {
      await stopInfoGathering(updatedInfo);
      return;
    }

    try {
      const question = await api.generateNextQuestion(infoGatheringNode, updatedInfo);
      if (question) {
        setCurrentQuestion(question);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `💭 ${question}`,
          isInfoGathering: true
        }]);
      } else {
        await stopInfoGathering(updatedInfo);
      }
    } catch (error) {
      console.error('Errore generazione domanda:', error);
      await stopInfoGathering(updatedInfo);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectedInfo, currentQuestion, infoGatheringNode, api]);

  /**
   * Stop info gathering and synthesize description
   */
  const stopInfoGathering = useCallback(async (finalInfo = collectedInfo) => {
    if (!infoGatheringNode || finalInfo.length === 0) {
      setInfoGatheringNode(null);
      setCollectedInfo([]);
      setCurrentQuestion('');
      return;
    }

    setMessages(prev => [...prev, {
      role: 'assistant',
      content: 'Sto sintetizzando...',
      isInfoGathering: true
    }]);

    try {
      const description = await api.synthesizeDescription(
        infoGatheringNode.title,
        infoGatheringNode.summary,
        finalInfo
      );

      setNodes(prev => prev.map(n =>
        n.id === infoGatheringNode.id
          ? { ...n, description }
          : n
      ));

      setMessages(prev => [
        ...prev.slice(0, -1),
        {
          role: 'assistant',
          content: '✅ Contesto acquisito. Le prossime idee saranno più mirate.'
        }
      ]);
    } catch (error) {
      console.error('Errore sintesi:', error);
      setMessages(prev => [
        ...prev.slice(0, -1),
        {
          role: 'assistant',
          content: '❌ Sintesi fallita, ma le risposte sono salve.'
        }
      ]);
    } finally {
      setInfoGatheringNode(null);
      setCollectedInfo([]);
      setCurrentQuestion('');
    }
  }, [infoGatheringNode, collectedInfo, api, setNodes, setMessages]);

  /**
   * Check if user input is stop command
   */
  const checkStopCommand = useCallback((input) => {
    return detectStopCommand(input);
  }, []);

  return {
    infoGatheringNode,
    currentQuestion,
    collectedInfo,
    isInfoGatheringActive: !!infoGatheringNode,
    startInfoGathering,
    answerQuestion,
    stopInfoGathering,
    checkStopCommand
  };
};
